import endpoints from '../endpoints.ts';
import {
  Customer,
  CustomerLocation,
  Quarantine,
  Toast,
  User,
} from '../types/models.ts';
import { PaginationOptions, WithPagination } from '../types/types.ts';
import { fetch } from './helpers.ts';

export namespace MedRequest {
  export interface CreateManySendPedigrees {
    tradingPartnerAddressId: number;
    medIds: number[];
    recipientEmailAddresses?: string[];
    customerLocationAddressId: number;
  }

  export interface CreateManyAmendPedigrees {
    tradingPartnerAddressId: number;
    medIds: number[];
    recipientEmailAddresses?: string[];
    customerLocationAddressId: number;
    documentIdentifier: string;
    datetimeCreated: string;
    verifiedTradingPartnerId: number;
  }

  export interface ReadOneBySerialNumberPedigree {
    serialNumber: string;
    locationId: number | string;
    ndc?: string;
    medName?: string;
    manufacturerName?: string;
    lotNumber?: string;
    startDate?: string;
    endDate?: string;
  }
}

export namespace MedAPI {
  export namespace Request {
    export interface ReadManyBySearch {
      values: {
        customerLocationId?: number;
        serialNumber?: string;
        ndc?: string;
        medName?: string;
        manufacturerName?: string;
        lotNumber?: string;
        startDate?: string;
        endDate?: string;
        poNumber?: string;
      };
      paginationOptions?: PaginationOptions;
    }

    export interface UpdateReceiveManyBySerialNumber {
      serialNumber: string;
      documentIds: number[] | string[];
    }

    export interface UpdateRecieveManyByEpcisMedVocabularyId {
      epcisMedVocabularyId: number | string;
      documentId: number | string;
    }

    export interface UpdateReceiveManyByShippingContainerCode {
      shippingContainerCode: string;
      documentId: number | string;
    }
  }
  export namespace Response {
    export interface ReadOneBySerialNumberPedigree {
      med: {
        id: number;
        serialNumber: string;
        lotNumber: string;
        expirationDate: string;
        dateReceived?: string;
        createdBy: {
          id: number;
          firstName: string;
          lastName: string;
        };
      };
      redbookDetail: {
        id: number;
        dateCreated: string;
        manufacturerName: string;
        medName: string;
      };
      customerLocation: {
        id: number;
        name: string;
      };
      redbook: {
        id: number;
        ndc: string;
      };
      isEpcisGenerated: true;
    }

    export interface readOneById {
      attachments: {
        id: number;
        contentType: string;
        filename: string;
        filepath: string;
        isArchived: boolean;
        medId: number;
        notes?: string;
        timestamp: string;
        uploaderId: number;
      }[];
      createdBy?: User;
      customer: Customer;
      customerLocation: CustomerLocation;
      med: {
        id: number;
        createdById?: number;
        customerLocationId: number;
        expirationDate: string;
        lotNumber: string;
        redbookDetailId: number;
        serialNumber: string;
      };
      redbook: { id: number; ndc: string };
      redbookDetail: {
        id: number;
        createdById?: number;
        customerId?: number;
        dateCreated?: string;
        manufacturerName: string;
        medName: string;
        redbookId?: number;
      };
    }
  }
}

const med = {
  create: {
    many: {
      sendPedigrees: async (
        params: MedRequest.CreateManySendPedigrees
      ): Promise<Toast | null> => {
        const url = endpoints.med.create.many.sendPedigrees();
        return await fetch<Toast>(url, 'POST', params);
      },
      amendPedigrees: async (
        params: MedRequest.CreateManyAmendPedigrees & {
          outboundDocumentId: number;
        }
      ): Promise<Toast | null> => {
        const url = endpoints.med.create.many.amendPedigrees();
        return await fetch<Toast>(url, 'POST', params);
      },
    },
  },
  read: {
    one: {
      by: {
        id: async (params: {
          medId: number | string;
          isEpcisGenerated: boolean;
        }): Promise<MedAPI.Response.readOneById | null> => {
          const url = endpoints.med.read.one.by.id();
          return await fetch<MedAPI.Response.readOneById>(url, 'GET', params);
        },
        serialNumber: {
          pedigree: async (
            params: MedRequest.ReadOneBySerialNumberPedigree
          ): Promise<MedAPI.Response.ReadOneBySerialNumberPedigree | null> => {
            const url = endpoints.med.read.one.by.serialNumber.pedigree();
            return await fetch<MedAPI.Response.ReadOneBySerialNumberPedigree>(
              url,
              'GET',
              params
            );
          },
        },
      },
    },
    many: {
      quarantine: async (params: MedAPI.Request.ReadManyBySearch) => {
        const url = endpoints.quarantine.read.many.by.search();
        return await fetch<
          WithPagination<{ meds: Quarantine[]; data: number }>
        >(url, 'GET', {
          ...params.values,
          ...params.paginationOptions,
        });
      },
      by: {
        search: async (params: MedAPI.Request.ReadManyBySearch) => {
          const url = endpoints.med.read.many.by.search();
          return await fetch<
            WithPagination<MedAPI.Response.ReadOneBySerialNumberPedigree>
          >(url, 'GET', {
            ...params.values,
            ...params.paginationOptions,
          });
        },
      },
    },
  },
  update: {
    one: {
      unverifyReceipt: async (params: {
        epcisSgtinId: number | string;
        documentId: number | string;
      }): Promise<Toast | null> => {
        const url = endpoints.med.update.one.unverifyReceipt();
        return fetch<Toast>(url, 'PUT', params);
      },
    },
    receive: {
      many: {
        by: {
          serialNumber: async (
            params: MedAPI.Request.UpdateReceiveManyBySerialNumber
          ): Promise<Toast | null> => {
            const url = endpoints.med.update.receive.many.by.serialNumber();
            return fetch<Toast>(url, 'PUT', params);
          },
          epcisMedVocabularyId: async (
            params: MedAPI.Request.UpdateRecieveManyByEpcisMedVocabularyId
          ): Promise<Toast | null> => {
            const url =
              endpoints.med.update.receive.many.by.epcisMedVocabularyId();
            return fetch<Toast>(url, 'PUT', params);
          },
          shippingContainerCode: async (
            params: MedAPI.Request.UpdateReceiveManyByShippingContainerCode
          ): Promise<Toast | null> => {
            const url =
              endpoints.med.update.receive.many.by.shippingContainerCode();
            return fetch<Toast>(url, 'PUT', params);
          },
        },
      },
      all: {
        by: {
          documentId: async (
            documentId: number | string
          ): Promise<Toast | null> => {
            const url = endpoints.med.update.receive.all.by.documentId();
            return fetch<Toast>(url, 'PUT', { documentId });
          },
        },
      },
    },
  },
  delete: {
    one: {
      attachment: (params: {
        attachmentId: number | string;
        isEpcisGenerated: boolean;
      }): Promise<Toast | null> => {
        const url = endpoints.med.delete.one.attachment();
        return fetch<Toast>(url, 'DELETE', params);
      },
    },
  },
};

export default med;
